/**
 * StudentBillingTab — embedded inside StudentDetail via render-prop injection.
 *
 * Shows:
 *  - Balance summary (owing / in-credit)
 *  - Invoice list filtered to this student (most recent first)
 *  - Each invoice expandable to show payment records
 */

import React, { useState } from 'react';
import styled from 'styled-components';

import {
  useInvoices,
  useStudentBalance,
  useStudentLedger,
  type InvoiceResponse,
} from '@features/invoices/api/billing';
import {
  INVOICE_STATUS_OPTIONS,
  formatMinorAmount,
} from '@features/invoices/config/ui';

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const BalanceRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BalanceTile = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  min-width: 140px;
`;

const TileLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TileValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ $color, theme }) => $color ?? theme.colors.text};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const SubText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusCell = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LedgerRow = styled.tr<{ $type?: string }>`
  background: ${({ $type }) =>
    $type === 'credit' ? '#f0fdf4' : $type === 'debit' ? '#fef2f2' : 'transparent'};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusColor(status: string): string {
  return INVOICE_STATUS_OPTIONS.find((o) => o.value === status)?.color ?? '#9ca3af';
}

function statusLabel(status: string): string {
  return INVOICE_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Invoice row (collapsible)
// ---------------------------------------------------------------------------

const InvoiceRow: React.FC<{ invoice: InvoiceResponse }> = ({ invoice }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        data-testid={`candeza-billing-invoice-row-${invoice.id}`}
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Td>
          <span style={{ fontWeight: 500 }}>{invoice.number ?? invoice.id.slice(0, 8)}</span>
          <SubText>{formatDate(invoice.issue_date ?? invoice.created_at)}</SubText>
        </Td>
        <Td>
          <StatusCell>
            <StatusDot $color={statusColor(invoice.status)} />
            {statusLabel(invoice.status)}
          </StatusCell>
        </Td>
        <Td style={{ textAlign: 'right' }}>{formatMinorAmount(invoice.total)}</Td>
        <Td style={{ textAlign: 'right' }}>{formatMinorAmount(invoice.amount_paid)}</Td>
        <Td style={{ textAlign: 'right', color: invoice.amount_due > 0 ? '#dc2626' : '#16a34a' }}>
          {formatMinorAmount(invoice.amount_due)}
        </Td>
        <Td style={{ color: '#64748b', fontSize: 12 }}>
          {expanded ? '▲' : '▼'}
        </Td>
      </tr>
      {expanded && invoice.line_items?.length > 0 && (
        <tr>
          <Td colSpan={6} style={{ background: '#f8fafc', paddingLeft: 32 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', color: '#64748b', padding: '4px 8px' }}>Item</th>
                  <th style={{ textAlign: 'right', color: '#64748b', padding: '4px 8px' }}>Qty</th>
                  <th style={{ textAlign: 'right', color: '#64748b', padding: '4px 8px' }}>Unit price</th>
                  <th style={{ textAlign: 'right', color: '#64748b', padding: '4px 8px' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((li, i) => (
                  <tr key={i}>
                    <td style={{ padding: '4px 8px' }}>{li.description}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{li.quantity}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{formatMinorAmount(li.unit_price)}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{formatMinorAmount(li.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Td>
        </tr>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

interface Props {
  studentId: string;
}

export const StudentBillingTab: React.FC<Props> = ({ studentId }) => {
  const { data: invoiceData, isLoading: invLoading, isError: invError } =
    useInvoices({ studentId, limit: 200 });

  const { data: balance, isLoading: balLoading } = useStudentBalance(studentId);
  const { data: ledger, isLoading: ledgerLoading } = useStudentLedger(studentId, 50);

  return (
    <div data-testid={`candeza-student-billing-tab-${studentId}`}>

      {/* Balance summary */}
      <Section>
        <SectionTitle>Balance</SectionTitle>
        <BalanceRow>
          <BalanceTile>
            <TileLabel>Outstanding</TileLabel>
            <TileValue $color={balance && balance.balance > 0 ? '#dc2626' : undefined}>
              {balLoading ? '…' : formatMinorAmount(balance?.balance ?? 0)}
            </TileValue>
          </BalanceTile>
        </BalanceRow>
      </Section>

      {/* Invoice list */}
      <Section>
        <SectionTitle>
          Invoices {invoiceData ? `(${invoiceData.items.length})` : ''}
        </SectionTitle>
        {invLoading && <EmptyState>Loading invoices…</EmptyState>}
        {invError && <EmptyState style={{ color: '#dc2626' }}>Failed to load invoices.</EmptyState>}
        {!invLoading && !invError && invoiceData?.items.length === 0 && (
          <EmptyState data-testid="candeza-student-billing-empty">No invoices found.</EmptyState>
        )}
        {!invLoading && !invError && (invoiceData?.items.length ?? 0) > 0 && (
          <Table data-testid="candeza-student-billing-table">
            <thead>
              <tr>
                <Th>Invoice</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Total</Th>
                <Th style={{ textAlign: 'right' }}>Paid</Th>
                <Th style={{ textAlign: 'right' }}>Due</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(invoiceData?.items ?? []).map((inv) => (
                <InvoiceRow key={inv.id} invoice={inv} />
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {/* Ledger */}
      {(ledger?.items.length ?? 0) > 0 && (
        <Section>
          <SectionTitle>Recent ledger entries</SectionTitle>
          <Table data-testid="candeza-student-ledger-table">
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Description</Th>
                <Th style={{ textAlign: 'right' }}>Debit</Th>
                <Th style={{ textAlign: 'right' }}>Credit</Th>
                <Th style={{ textAlign: 'right' }}>Running balance</Th>
              </tr>
            </thead>
            <tbody>
              {ledgerLoading && (
                <tr><Td colSpan={5}>Loading…</Td></tr>
              )}
              {(ledger?.items ?? []).map((entry) => (
                <LedgerRow
                  key={entry.id}
                  $type={entry.entry_type}
                  data-testid={`candeza-ledger-row-${entry.id}`}
                >
                  <Td>{formatDate(entry.created_at)}</Td>
                  <Td>{entry.notes ?? '—'}</Td>
                  <Td style={{ textAlign: 'right', color: '#dc2626' }}>
                    {entry.entry_type === 'invoice' ? formatMinorAmount(entry.amount) : '—'}
                  </Td>
                  <Td style={{ textAlign: 'right', color: '#16a34a' }}>
                    {entry.entry_type === 'payment' ? formatMinorAmount(Math.abs(entry.amount)) : '—'}
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {formatMinorAmount(entry.balance_after)}
                  </Td>
                </LedgerRow>
              ))}
            </tbody>
          </Table>
        </Section>
      )}
    </div>
  );
};
